import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { verifyContactLead, submitContactLead, submitDownloadLead } from './utils/leadsAPI';
import { buildInternationalPhone } from './utils/phone';
import WheelTimePicker from './components/WheelTimePicker';
import iconApp from './assets/images/iconApp.png';

const COUNTRY_CODE_OPTIONS = [
  { value: '+972', label: '🇮🇱 ישראל (+972)' },
  { value: '+1', label: '🇺🇸 ארה"ב (+1)' },
  { value: '+44', label: '🇬🇧 בריטניה (+44)' },
  { value: '+49', label: '🇩🇪 גרמניה (+49)' },
  { value: '+33', label: '🇫🇷 צרפת (+33)' },
  { value: '+39', label: '🇮🇹 איטליה (+39)' },
  { value: '+34', label: '🇪🇸 ספרד (+34)' },
  { value: '+31', label: '🇳🇱 הולנד (+31)' },
  { value: '+41', label: '🇨🇭 שוויץ (+41)' },
  { value: '+43', label: '🇦🇹 אוסטריה (+43)' },
  { value: '+32', label: '🇧🇪 בלגיה (+32)' },
  { value: '+46', label: '🇸🇪 שוודיה (+46)' },
  { value: '+47', label: '🇳🇴 נורווגיה (+47)' },
  { value: '+45', label: '🇩🇰 דנמרק (+45)' },
  { value: '+358', label: '🇫🇮 פינלנד (+358)' },
  { value: '+7', label: '🇷🇺 רוסיה (+7)' },
  { value: '+86', label: '🇨🇳 סין (+86)' },
  { value: '+81', label: '🇯🇵 יפן (+81)' },
  { value: '+82', label: '🇰🇷 דרום קוריאה (+82)' },
  { value: '+91', label: '🇮🇳 הודו (+91)' },
  { value: '+61', label: '🇦🇺 אוסטרליה (+61)' },
  { value: '+64', label: '🇳🇿 ניו זילנד (+64)' },
  { value: '+27', label: '🇿🇦 דרום אפריקה (+27)' },
  { value: '+971', label: '🇦🇪 איחוד האמירויות (+971)' },
  { value: '+966', label: '🇸🇦 ערב הסעודית (+966)' },
  { value: '+20', label: '🇪🇬 מצרים (+20)' },
  { value: '+90', label: '🇹🇷 טורקיה (+90)' },
  { value: '+351', label: '🇵🇹 פורטוגל (+351)' },
  { value: '+353', label: '🇮🇪 אירלנד (+353)' },
  { value: '+48', label: '🇵🇱 פולין (+48)' },
  { value: '+420', label: '🇨🇿 צ\'כיה (+420)' },
  { value: '+36', label: '🇭🇺 הונגריה (+36)' },
  { value: '+40', label: '🇷🇴 רומניה (+40)' },
  { value: '+30', label: '🇬🇷 יוון (+30)' },
];

const FEATURES = [
  {
    icon: '💡',
    title: 'תובנות וכלים פרקטיים',
    text: 'מאמרים, תובנות וכלים מעשיים ליישום בשטח — מותאמים למציאות של מנכ״לים.',
  },
  {
    icon: '💬',
    title: 'שיתוף בין מנכ״לים',
    text: 'מרחב פתוח להעלאת דילמות, אתגרים והזדמנויות — בלי לבד על הפסגה.',
  },
  {
    icon: '👥',
    title: 'חברי הפאנל',
    text: 'ראו מי בפאנל, למדו מהניסיון של אחרים והעמיקו חיבורים אמיתיים.',
  },
  {
    icon: '🤝',
    title: 'אג׳נדה ומפגשים',
    text: 'סדר יום ברור למפגש הקרוב, סיכומים ותובנות שנשארים איתכם אחריו.',
  },
  {
    icon: '🪞',
    title: 'חזון, ייעוד וערכים',
    text: 'מה מניע ומנחה את הפאנל — נקודת עוגן משותפת לצמיחה אישית ועסקית.',
  },
  {
    icon: '🧭',
    title: 'התבוננות שבועית',
    text: 'הזדמנות לעצור, להתבונן ולדייק את המציאות האישית והעסקית.',
  },
];

function App() {
  const [formMode, setFormMode] = useState('download');
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [isSubmittingDownload, setIsSubmittingDownload] = useState(false);
  const [showVerificationScreen, setShowVerificationScreen] = useState(false);
  const [verificationPurpose, setVerificationPurpose] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [enteredCode, setEnteredCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [successPopup, setSuccessPopup] = useState(null);
  const codeInputRefs = useRef([]);

  const [contactForm, setContactForm] = useState({
    fullName: '',
    contactMethod: 'phone',
    phone: '',
    countryCode: '+972',
    email: '',
    availableTimes: [{ date: '', fromTime: '', toTime: '' }],
  });

  const [downloadForm, setDownloadForm] = useState({
    fullName: '',
    devicePlatform: 'iphone',
    phone: '',
    countryCode: '+972',
    email: '',
  });

  useEffect(() => {
    if (showVerificationScreen && codeInputRefs.current[0]) {
      setTimeout(() => codeInputRefs.current[0]?.focus(), 100);
    }
  }, [showVerificationScreen]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('animate-in');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const animatedElements = document.querySelectorAll(
      '.scroll-animate, .scroll-animate-left, .scroll-animate-right, .scroll-animate-scale'
    );
    animatedElements.forEach((el) => observer.observe(el));
    return () => animatedElements.forEach((el) => observer.unobserve(el));
  }, []);

  const handleContactFormChange = (field, value) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleDownloadFormChange = (field, value) => {
    setDownloadForm((prev) => ({ ...prev, [field]: value }));
  };

  const activeVerificationForm =
    verificationPurpose === 'download' ? downloadForm : contactForm;

  const clearVerificationState = () => {
    setShowVerificationScreen(false);
    setVerificationPurpose(null);
    setVerificationCode('');
    setEnteredCode('');
  };

  const openSuccessPopup = ({ name, purpose }) => {
    clearVerificationState();
    setSuccessPopup({
      name: String(name || '').trim() || 'אורח/ת',
      purpose,
    });
  };

  const closeSuccessPopup = () => setSuccessPopup(null);

  const addTimeSlot = () => {
    setContactForm((prev) => ({
      ...prev,
      availableTimes: [...prev.availableTimes, { date: '', fromTime: '', toTime: '' }],
    }));
  };

  const updateTimeSlot = (index, field, value) => {
    setContactForm((prev) => ({
      ...prev,
      availableTimes: prev.availableTimes.map((timeSlot, i) => {
        if (i !== index) return timeSlot;
        const next = { ...timeSlot, [field]: value };
        if (field === 'fromTime' && next.toTime && value && next.toTime < value) {
          next.toTime = '';
        }
        return next;
      }),
    }));
  };

  const removeTimeSlot = (index) => {
    setContactForm((prev) => ({
      ...prev,
      availableTimes: prev.availableTimes.filter((_, i) => i !== index),
    }));
  };

  const handleDownloadSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingDownload) return;
    setIsSubmittingDownload(true);
    try {
      const verifyResult = await verifyContactLead({
        preferredContactMethod: 'email',
        email: downloadForm.email?.trim(),
      });
      if (verifyResult.success) {
        setVerificationPurpose('download');
        setVerificationCode(verifyResult.data.verificationCode);
        setShowVerificationScreen(true);
      } else {
        throw new Error(verifyResult.message || 'שגיאה בשליחת קוד האימות');
      }
    } catch (error) {
      console.error('Error sending download verification code:', error);
      alert(`❌ ${error.message || 'אופס! משהו השתבש בשליחת קוד האימות. אנא נסה שנית.'}`);
    } finally {
      setIsSubmittingDownload(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingContact) return;
    setIsSubmittingContact(true);
    try {
      const phone =
        contactForm.contactMethod === 'phone'
          ? buildInternationalPhone(contactForm.countryCode, contactForm.phone)
          : undefined;
      const email =
        contactForm.contactMethod === 'email' ? contactForm.email?.trim() : undefined;

      const verifyResult = await verifyContactLead({
        preferredContactMethod: contactForm.contactMethod,
        phone,
        email,
      });

      if (verifyResult.success) {
        setVerificationPurpose('talk');
        setVerificationCode(verifyResult.data.verificationCode);
        setShowVerificationScreen(true);
      } else {
        throw new Error(verifyResult.message || 'שגיאה בשליחת קוד האימות');
      }
    } catch (error) {
      console.error('Error sending verification code:', error);
      alert(`❌ ${error.message || 'אופס! משהו השתבש בשליחת קוד האימות. אנא נסה שנית.'}`);
    } finally {
      setIsSubmittingContact(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    if (isVerifying) return;
    setIsVerifying(true);

    try {
      if (enteredCode.trim() !== verificationCode) {
        alert('❌ קוד האימות שגוי. אנא נסה שנית.');
        setEnteredCode('');
        return;
      }

      if (verificationPurpose === 'download') {
        const result = await submitDownloadLead({
          fullName: downloadForm.fullName.trim(),
          devicePlatform: downloadForm.devicePlatform,
          preferredContactMethod: 'email',
          email: downloadForm.email?.trim(),
          phone: downloadForm.phone?.trim()
            ? buildInternationalPhone(downloadForm.countryCode, downloadForm.phone)
            : undefined,
          requestType: 'appDownload',
          appVariant: 'ceo',
        });
        if (!result.success) throw new Error(result.message || 'שגיאה בשליחת הטופס');

        const name = downloadForm.fullName.trim();
        setDownloadForm({
          fullName: '',
          devicePlatform: 'iphone',
          phone: '',
          countryCode: '+972',
          email: '',
        });
        openSuccessPopup({ name, purpose: 'download' });
        return;
      }

      const formattedTimes = contactForm.availableTimes
        .filter((timeSlot) => timeSlot.date && timeSlot.fromTime)
        .map((timeSlot) => {
          const fromTimeTrimmed = timeSlot.fromTime.trim();
          const toTimeTrimmed = timeSlot.toTime?.trim();
          const timeSlotData = { date: timeSlot.date, fromTime: fromTimeTrimmed };
          if (toTimeTrimmed && toTimeTrimmed !== fromTimeTrimmed) {
            timeSlotData.toTime = toTimeTrimmed;
          }
          return timeSlotData;
        });

      const result = await submitContactLead({
        fullName: contactForm.fullName.trim(),
        preferredContactMethod: contactForm.contactMethod,
        phone:
          contactForm.contactMethod === 'phone'
            ? buildInternationalPhone(contactForm.countryCode, contactForm.phone)
            : undefined,
        email:
          contactForm.contactMethod === 'email' ? contactForm.email?.trim() : undefined,
        availableTimes: formattedTimes,
        appVariant: 'ceo',
      });

      if (!result.success) throw new Error(result.message || 'שגיאה בשליחת הטופס');

      const name = contactForm.fullName.trim();
      setContactForm({
        fullName: '',
        contactMethod: 'phone',
        phone: '',
        countryCode: '+972',
        email: '',
        availableTimes: [{ date: '', fromTime: '', toTime: '' }],
      });
      openSuccessPopup({ name, purpose: 'talk' });
    } catch (error) {
      console.error('Error submitting verified form:', error);
      alert(`❌ ${error.message || 'אופס! משהו השתבש. אנא נסה שנית או צור קשר ישירות.'}`);
    } finally {
      setIsVerifying(false);
    }
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="App" dir="rtl">
      <div className="company-header">
        <div className="container">
          <div className="header-content">
            <div className="company-logo">
              <img src={iconApp} alt="Great Leaders CEO" className="logo-image" />
              <div className="ceo-brand-block">
                <h1 className="company-name">Great Leaders</h1>
                <span className="ceo-badge">CEO</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="philosophy-section scroll-animate">
        <div className="container">
          <div className="philosophy-content">
            <h2 className="philosophy-title">המקום של מנכ״לים שמנהלים לבד — וצומחים ביחד</h2>
            <p className="philosophy-text">
              בתפקיד המנכ״ל אתם לבד בקצה. האחריות כולה עליכם — אבל הערך האמיתי נולד כשיש
              פאנל של מנכ״לים שחולקים דילמות, תובנות ושיתופיות אמיתית.
            </p>
            <div className="mission-section">
              <h3 className="mission-title">מה זה Great Leaders CEO?</h3>
              <p className="mission-text">
                אפליקציה לניהול פאנלים של מנכ״לים: כל מנכ״ל נשאר עצמאי בתפקידו, ובמקביל
                מקבל ערך משיתוף, השראה וחיבור עם מנכ״לים אחרים — בתוך קבוצה סגורה ומנוהלת.
              </p>
            </div>
            <div className="vision-section">
              <h3 className="vision-title">החזון שלנו</h3>
              <p className="vision-text">
                עולם שבו מנכ״לים לא נשארים לבד עם האתגרים הגדולים — אלא בונים יחד מרחב
                של אמון, למידה הדדית והחלטות טובות יותר.
              </p>
            </div>
            <div className="values-section">
              <h3 className="values-title">עקרונות הפאנל</h3>
              <div className="values-grid">
                <div className="value-item">
                  <span className="value-icon">🧍</span>
                  <h4>עצמאות</h4>
                  <p>כל מנכ״ל מוביל את הארגון שלו — בלי תלות, עם מרחב אישי</p>
                </div>
                <div className="value-item">
                  <span className="value-icon">🔗</span>
                  <h4>שיתופיות</h4>
                  <p>דילמות, אתגרים והזדמנויות שעולים יחד — ומתחדדים יחד</p>
                </div>
                <div className="value-item">
                  <span className="value-icon">✨</span>
                  <h4>השראה</h4>
                  <p>תובנות מהשטח, מניסיון אמיתי של מנכ״לים שכבר היו שם</p>
                </div>
                <div className="value-item">
                  <span className="value-icon">🔒</span>
                  <h4>אמון</h4>
                  <p>פאנל סגור, שיח כנה, ומרחב בטוח לדבר על מה שבאמת חשוב</p>
                </div>
              </div>
            </div>
            <div className="ceo-hero-cta">
              <button type="button" className="pricing-button" onClick={scrollToContact}>
                הצטרפו לפאנל / הורידו את האפליקציה
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="benefits-section scroll-animate">
        <div className="container">
          <h2 className="section-title">מה תמצאו באפליקציה?</h2>
          <div className="benefits-grid ceo-features-grid">
            {FEATURES.map((feature) => (
              <div key={feature.title} className="benefit-card scroll-animate-scale">
                <div className="benefit-icon">{feature.icon}</div>
                <h3>{feature.title}</h3>
                <p>{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="experience-section scroll-animate">
        <div className="container">
          <h2 className="section-title">איך זה עובד?</h2>
          <div className="ceo-steps">
            <div className="ceo-step scroll-animate-left">
              <span className="ceo-step-num">01</span>
              <h3>מצטרפים לפאנל</h3>
              <p>מנכ״לים מוזמנים לקבוצה סגורה ומנוהלת — לא רשת פתוחה, אלא מעגל אמון.</p>
            </div>
            <div className="ceo-step scroll-animate">
              <span className="ceo-step-num">02</span>
              <h3>משתפים ומקבלים ערך</h3>
              <p>דילמות, תובנות ומפגשים — כל אחד מביא את הניסיון שלו ומקבל בחזרה פרספקטיבה.</p>
            </div>
            <div className="ceo-step scroll-animate-right">
              <span className="ceo-step-num">03</span>
              <h3>צומחים לבד — וביחד</h3>
              <p>אתם ממשיכים להוביל לבד בארגון, עם גב של פאנל שמבין בדיוק את הגובה של התפקיד.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="app-download-section scroll-animate">
        <div className="container">
          <div className="contact-section-heading">
            <h2 className="section-title">מוכנים להתחיל?</h2>
            <p className="contact-section-subtitle">
              הורידו את האפליקציה או השאירו פרטים לשיחה — ונחזור אליכם
            </p>
          </div>

          <div className="contact-mode-toggle" role="tablist" aria-label="בחירת סוג פנייה">
            <button
              type="button"
              role="tab"
              aria-selected={formMode === 'download'}
              className={`contact-mode-btn${formMode === 'download' ? ' active' : ''}`}
              onClick={() => setFormMode('download')}
            >
              רוצה להוריד את האפליקציה
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={formMode === 'talk'}
              className={`contact-mode-btn${formMode === 'talk' ? ' active' : ''}`}
              onClick={() => setFormMode('talk')}
            >
              רוצים לדבר איתנו
            </button>
          </div>

          <div className="contact-inline-form">
            {formMode === 'download' ? (
              <form className="download-link-form" onSubmit={handleDownloadSubmit}>
                <div className="form-group form-group--name">
                  <label htmlFor="download-fullName">שם מלא</label>
                  <div className="input-shell">
                    <span className="input-shell-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </span>
                    <input
                      id="download-fullName"
                      type="text"
                      value={downloadForm.fullName}
                      onChange={(e) => handleDownloadFormChange('fullName', e.target.value)}
                      placeholder="איך קוראים לכם?"
                      required
                    />
                  </div>
                </div>

                <div className="form-group form-group--device">
                  <label>איזה מכשיר יש לך?</label>
                  <div className="radio-group">
                    <label
                      className={`contact-method-option${
                        downloadForm.devicePlatform === 'iphone' ? ' selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="downloadDevicePlatform"
                        value="iphone"
                        checked={downloadForm.devicePlatform === 'iphone'}
                        onChange={(e) =>
                          handleDownloadFormChange('devicePlatform', e.target.value)
                        }
                      />
                      <span className="method-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="7" y="2" width="10" height="20" rx="2" ry="2" />
                          <line x1="12" y1="18" x2="12.01" y2="18" />
                        </svg>
                      </span>
                      <span className="method-text">
                        <span className="method-title">אייפון</span>
                        <span className="method-hint">iOS / App Store</span>
                      </span>
                    </label>
                    <label
                      className={`contact-method-option${
                        downloadForm.devicePlatform === 'android' ? ' selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="downloadDevicePlatform"
                        value="android"
                        checked={downloadForm.devicePlatform === 'android'}
                        onChange={(e) =>
                          handleDownloadFormChange('devicePlatform', e.target.value)
                        }
                      />
                      <span className="method-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                          <line x1="12" y1="18" x2="12.01" y2="18" />
                        </svg>
                      </span>
                      <span className="method-text">
                        <span className="method-title">אנדרואיד</span>
                        <span className="method-hint">Google Play</span>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="form-group form-group--contact">
                  <label htmlFor="download-email">מייל שמחובר לחנות האפליקציות בטלפון</label>
                  <div className="input-shell">
                    <span className="input-shell-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </span>
                    <input
                      id="download-email"
                      type="email"
                      value={downloadForm.email}
                      onChange={(e) => handleDownloadFormChange('email', e.target.value)}
                      placeholder="name@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="form-group form-group--contact">
                  <label htmlFor="download-phone">
                    טלפון <span className="optional-label">(אופציונלי)</span>
                  </label>
                  <div className="phone-input-container">
                    <div className="input-shell phone-number-shell">
                      <input
                        id="download-phone"
                        type="tel"
                        value={downloadForm.phone}
                        onChange={(e) => handleDownloadFormChange('phone', e.target.value)}
                        placeholder="מספר טלפון"
                      />
                    </div>
                    <select
                      value={downloadForm.countryCode}
                      onChange={(e) =>
                        handleDownloadFormChange('countryCode', e.target.value)
                      }
                      className="country-code-select"
                      aria-label="קידומת מדינה"
                    >
                      {COUNTRY_CODE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  className="submit-contact"
                  disabled={isSubmittingDownload}
                >
                  <span className="submit-contact-label">
                    {isSubmittingDownload ? 'שולח...' : 'שלח לי לינק'}
                  </span>
                  {!isSubmittingDownload && (
                    <span className="submit-contact-arrow" aria-hidden="true">
                      ←
                    </span>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleContactSubmit}>
                <div className="form-group form-group--name">
                  <label htmlFor="contact-fullName">שם מלא</label>
                  <div className="input-shell">
                    <span className="input-shell-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </span>
                    <input
                      id="contact-fullName"
                      type="text"
                      value={contactForm.fullName}
                      onChange={(e) => handleContactFormChange('fullName', e.target.value)}
                      placeholder="איך קוראים לכם?"
                      required
                    />
                  </div>
                </div>

                <div className="form-group form-group--method">
                  <label>אמצעי יצירת קשר מועדף</label>
                  <div className="radio-group">
                    <label
                      className={`contact-method-option${
                        contactForm.contactMethod === 'phone' ? ' selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="contactMethod"
                        value="phone"
                        checked={contactForm.contactMethod === 'phone'}
                        onChange={(e) =>
                          handleContactFormChange('contactMethod', e.target.value)
                        }
                      />
                      <span className="method-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                      </span>
                      <span className="method-text">
                        <span className="method-title">טלפון</span>
                        <span className="method-hint">שיחה או הודעה</span>
                      </span>
                    </label>
                    <label
                      className={`contact-method-option${
                        contactForm.contactMethod === 'email' ? ' selected' : ''
                      }`}
                    >
                      <input
                        type="radio"
                        name="contactMethod"
                        value="email"
                        checked={contactForm.contactMethod === 'email'}
                        onChange={(e) =>
                          handleContactFormChange('contactMethod', e.target.value)
                        }
                      />
                      <span className="method-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                      </span>
                      <span className="method-text">
                        <span className="method-title">מייל</span>
                        <span className="method-hint">תכתובת נוחה</span>
                      </span>
                    </label>
                  </div>
                </div>

                {contactForm.contactMethod === 'phone' ? (
                  <div className="form-group form-group--contact">
                    <label htmlFor="contact-phone">טלפון</label>
                    <div className="phone-input-container">
                      <div className="input-shell phone-number-shell">
                        <input
                          id="contact-phone"
                          type="tel"
                          value={contactForm.phone}
                          onChange={(e) => handleContactFormChange('phone', e.target.value)}
                          placeholder="מספר טלפון"
                          required
                        />
                      </div>
                      <select
                        value={contactForm.countryCode}
                        onChange={(e) =>
                          handleContactFormChange('countryCode', e.target.value)
                        }
                        className="country-code-select"
                        aria-label="קידומת מדינה"
                      >
                        {COUNTRY_CODE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="form-group form-group--contact">
                    <label htmlFor="contact-email">מייל</label>
                    <div className="input-shell">
                      <span className="input-shell-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                      </span>
                      <input
                        id="contact-email"
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => handleContactFormChange('email', e.target.value)}
                        placeholder="name@example.com"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="form-group form-group--times">
                  <label className="times-main-label">זמנים פנויים</label>
                  {contactForm.availableTimes.map((timeSlot, index) => (
                    <div key={index} className="time-slot-container" style={{ '--slot-i': index }}>
                      <div className="time-slot-header">
                        <h4 className="time-slot-title">
                          <span className="time-slot-badge">{index + 1}</span>
                          זמן פנוי
                        </h4>
                        {contactForm.availableTimes.length > 1 && (
                          <button
                            type="button"
                            className="remove-time"
                            onClick={() => removeTimeSlot(index)}
                            title="הסר זמן"
                          >
                            ✕
                          </button>
                        )}
                      </div>

                      <div className="date-picker-section">
                        <label className="time-label">תאריך</label>
                        <input
                          type="date"
                          value={timeSlot.date}
                          onChange={(e) => updateTimeSlot(index, 'date', e.target.value)}
                          className="date-input"
                          min={new Date().toISOString().split('T')[0]}
                          required
                        />
                      </div>

                      <div className="time-pickers-section">
                        <div className="time-picker-group">
                          <WheelTimePicker
                            value={timeSlot.fromTime}
                            onChange={(value) => updateTimeSlot(index, 'fromTime', value)}
                            label="משעה"
                            required
                          />
                        </div>
                        <div className="time-picker-group">
                          <WheelTimePicker
                            value={timeSlot.toTime}
                            onChange={(value) => updateTimeSlot(index, 'toTime', value)}
                            label="עד שעה"
                            disabled={!timeSlot.fromTime}
                            minTime={timeSlot.fromTime}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="add-time-button" onClick={addTimeSlot}>
                    <span className="add-icon">+</span>
                    הוסף זמן נוסף
                  </button>
                </div>

                <button
                  type="submit"
                  className="submit-contact"
                  disabled={isSubmittingContact}
                >
                  <span className="submit-contact-label">
                    {isSubmittingContact ? 'שולח...' : 'שלחו אלינו'}
                  </span>
                  {!isSubmittingContact && (
                    <span className="submit-contact-arrow" aria-hidden="true">
                      ←
                    </span>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <footer className="footer scroll-animate">
        <div className="container">
          <p>אל תישארו לבד בתפקיד — הצטרפו לפאנל מנכ״לים שמבין את הגובה. 🌍</p>
          <p className="footer-note">נשמח לשמוע מכם! 🚀</p>
        </div>
      </footer>

      {showVerificationScreen && (
        <div
          className="contact-popup-overlay verification-overlay"
          onClick={clearVerificationState}
        >
          <div className="contact-popup verification-popup" onClick={(e) => e.stopPropagation()}>
            <button className="close-popup" onClick={clearVerificationState}>
              ✕
            </button>

            <div className="verification-header">
              <div className="verification-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#ffc915" strokeWidth="2" fill="none" />
                  <path
                    d="M8 12l2 2 4-4"
                    stroke="#ffc915"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="verification-title">אימות קשר</h2>
              <p className="verification-subtitle">
                {verificationPurpose === 'download'
                  ? `שלחנו קוד אימות למייל ${downloadForm.email}`
                  : activeVerificationForm.contactMethod === 'email'
                    ? `שלחנו קוד אימות למייל ${activeVerificationForm.email}`
                    : `שלחנו קוד אימות למספר ${buildInternationalPhone(
                        activeVerificationForm.countryCode,
                        activeVerificationForm.phone
                      )}`}
              </p>
            </div>

            <form onSubmit={handleVerificationSubmit} className="verification-form">
              <div className="verification-code-container">
                <label className="verification-label">קוד האימות</label>
                <div className="code-input-wrapper">
                  {[0, 1, 2, 3].map((index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        codeInputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      className={`code-digit${enteredCode[index] ? ' has-value' : ''}`}
                      value={enteredCode[index] || ''}
                      aria-label={`ספרה ${index + 1} מתוך 4`}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 1);
                        if (value) {
                          const newCode = enteredCode.split('');
                          newCode[index] = value;
                          const updatedCode = newCode.join('').slice(0, 4);
                          setEnteredCode(updatedCode);
                          if (index < 3 && codeInputRefs.current[index + 1]) {
                            codeInputRefs.current[index + 1].focus();
                          }
                        } else {
                          const newCode = enteredCode.split('');
                          newCode[index] = '';
                          setEnteredCode(newCode.join(''));
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Backspace' && !enteredCode[index] && index > 0) {
                          codeInputRefs.current[index - 1]?.focus();
                        } else if (e.key === 'ArrowLeft' && index > 0) {
                          e.preventDefault();
                          codeInputRefs.current[index - 1]?.focus();
                        } else if (e.key === 'ArrowRight' && index < 3) {
                          e.preventDefault();
                          codeInputRefs.current[index + 1]?.focus();
                        }
                      }}
                      onPaste={(e) => {
                        e.preventDefault();
                        const pastedData = e.clipboardData
                          .getData('text')
                          .replace(/\D/g, '')
                          .slice(0, 4);
                        if (pastedData) {
                          setEnteredCode(pastedData);
                          const nextIndex = Math.min(index + pastedData.length, 3);
                          codeInputRefs.current[nextIndex]?.focus();
                        }
                      }}
                      maxLength="1"
                      required
                    />
                  ))}
                </div>
                <p className="code-hint">הכנס את הקוד בן 4 הספרות שקיבלת</p>
              </div>

              <button
                type="submit"
                className="verify-submit-btn"
                disabled={isVerifying || enteredCode.length !== 4}
              >
                {isVerifying ? (
                  <>
                    <span className="spinner" />
                    מאמת...
                  </>
                ) : (
                  <>
                    <span>✓</span>
                    אמת ושלח
                  </>
                )}
              </button>

              <button type="button" className="back-to-form-btn" onClick={clearVerificationState}>
                ← חזור לטופס
              </button>
            </form>
          </div>
        </div>
      )}

      {successPopup && (
        <div className="contact-popup-overlay success-popup-overlay" onClick={closeSuccessPopup}>
          <div
            className="contact-popup success-popup"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="success-popup-title"
          >
            <button
              type="button"
              className="close-popup"
              onClick={closeSuccessPopup}
              aria-label="סגירה"
            >
              ✕
            </button>

            <div className="success-popup-icon" aria-hidden="true">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="#ffc915" strokeWidth="2" fill="none" />
                <path
                  d="M8 12l2 2 4-4"
                  stroke="#383c7f"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h2 id="success-popup-title" className="success-popup-title">
              היי {successPopup.name}!
            </h2>
            <p className="success-popup-message">
              {successPopup.purpose === 'download'
                ? 'הפרטים התקבלו בהצלחה. נחזור אליך עם לינק להורדת האפליקציה בהקדם.'
                : 'הפרטים התקבלו בהצלחה. ניצור איתך קשר בהקדם האפשרי.'}
            </p>

            <button
              type="button"
              className="success-popup-close-btn"
              onClick={closeSuccessPopup}
            >
              סגירה
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
